<template>
  <div class="video-uploader-container">
    <!-- 视频上传框列表 -->
    <div class="upload-box-list">
      <!-- 已上传的视频框 -->
      <div
          v-for="(url, index) in videoUrls"
          :key="index"
          :style="{ width: boxWidth + 'px', height: boxHeight + 'px' }"
          class="upload-box"
      >
        <video
            :src="url"
            class="preview-video"
            @click="handleVideoClick(url)"
        ></video>
        <div class="play-icon" @click="handleVideoClick(url)">
          <play-circle-outlined />
        </div>
        <div v-if="onlyPreview === false" class="delete-btn" @click.stop="handleDelete(index)">
          <delete-outlined />
        </div>
      </div>

      <!-- 上传中框 -->
      <div
          v-if="uploading"
          :style="{ width: boxWidth + 'px', height: boxHeight + 'px' }"
          class="upload-box upload-loading"
      >
        <a-progress
            type="circle"
            :percent="uploadPercent"
            :width="50"
        />
        <p class="loading-text">上传中...</p>
      </div>

      <!-- 新增上传框 -->
      <div
          v-if="onlyPreview === false && !uploading && videoUrls.length < maxCount"
          :style="{ width: boxWidth + 'px', height: boxHeight + 'px' }"
          class="upload-box upload-area"
          @click="handleUploadClick"
      >
        <div class="upload-hint">
          <plus-outlined class="plus-icon" />
          <p class="hint-text">点击上传视频</p>
        </div>
      </div>
    </div>

    <!-- 视频预览弹窗 -->
    <a-modal
        v-model:open="previewVisible"
        :footer="null"
        :width="800"
        @cancel="handlePreviewClose"
    >
      <div class="preview-modal-content">
        <video
            v-if="previewVideoUrl"
            :src="previewVideoUrl"
            controls
            autoplay
            class="modal-video"
        ></video>
      </div>
    </a-modal>

    <!-- 隐藏的上传组件 -->
    <a-upload
        name="file"
        :action="''"
        :beforeUpload="beforeUpload"
        :showUploadList="false"
        accept="video/*"
        ref="uploadRef"
        :multiple="false"
    />
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits, watch, defineExpose } from 'vue';
import { Upload, message } from 'ant-design-vue';
import { PlusOutlined, DeleteOutlined, PlayCircleOutlined } from '@ant-design/icons-vue';
import { uploadOneFile } from '@/api/upload.js';

// 定义属性
const props = defineProps({
  // 视频路径数组
  videoUrls: {
    type: Array,
    default: () => []
  },
  // 上传路径名称
  pathName: {
    type: String,
    default: 'video'
  },
  // 最大上传数量
  maxCount: {
    type: Number,
    default: 5
  },
  // 上传框宽度
  boxWidth: {
    type: Number,
    default: 200
  },
  // 上传框高度
  boxHeight: {
    type: Number,
    default: 150
  },
  // 是否仅预览
  onlyPreview: {
    type: Boolean,
    default: false
  }
});

// 定义事件
const emits = defineEmits(['update:videoUrls', 'uploadSuccess', 'delete']);

// 引用
const uploadRef = ref(null);
const localVideoUrls = ref([...props.videoUrls]);

// 预览相关状态
const previewVisible = ref(false);
const previewVideoUrl = ref('');

// 上传状态
const uploading = ref(false);
const uploadPercent = ref(0);

// 处理上传点击
const handleUploadClick = () => {
  if (uploadRef.value && localVideoUrls.value.length < props.maxCount) {
    uploadRef.value.$el.querySelector('input[type="file"]').click();
  } else if (localVideoUrls.value.length >= props.maxCount) {
    message.warning(`最多只能上传${props.maxCount}个视频`);
  }
};

// 处理删除
const handleDelete = (index) => {
  const newUrls = [...localVideoUrls.value];
  newUrls.splice(index, 1);
  localVideoUrls.value = newUrls;
  emits('update:videoUrls', newUrls);
  emits('delete', index);
};

// 处理视频点击预览
const handleVideoClick = (url) => {
  previewVideoUrl.value = url;
  previewVisible.value = true;
};

// 关闭预览
const handlePreviewClose = () => {
  previewVisible.value = false;
  previewVideoUrl.value = '';
};

// 上传前处理
const beforeUpload = (file) => {
  // 检查文件类型
  const isVideo = file.type.startsWith('video/');
  if (!isVideo) {
    message.error('仅支持视频格式的文件!');
    return Upload.LIST_IGNORE;
  }

  // 检查文件大小
  const isLt500M = file.size / 1024 / 1024 < 500;
  if (!isLt500M) {
    message.error('视频大小不能超过 500MB!');
    return Upload.LIST_IGNORE;
  }

  // 检查是否超过最大数量
  if (localVideoUrls.value.length >= props.maxCount) {
    message.warning(`最多只能上传${props.maxCount}个视频`);
    return Upload.LIST_IGNORE;
  }

  // 开始上传
  uploading.value = true;
  uploadPercent.value = 0;

  const formData = convertFormData(file);
  uploadOneFile(formData, (progressEvent) => {
    if (progressEvent.total) {
      uploadPercent.value = Math.round((progressEvent.loaded / progressEvent.total) * 100);
    }
  })
      .then(res => {
        if (res && res.data) {
          const newUrls = [...localVideoUrls.value, res.data];
          localVideoUrls.value = newUrls;
          emits('update:videoUrls', newUrls);
          emits('uploadSuccess', res.data, newUrls);
          message.success('上传成功');
        }
      })
      .catch(err => {
        message.error(`上传失败: ${err.message || '未知错误'}`);
      })
      .finally(() => {
        uploading.value = false;
        uploadPercent.value = 0;
      });

  return false; // 阻止自动上传
};

// 转换为FormData
const convertFormData = (file) => {
  const folder = Date.now().toString();
  const json = {
    bucketName: 'testing-management-public',
    pathName: props.pathName + '/' + folder,
  };
  const formData = new FormData();
  formData.append('uploadFile', file);
  formData.append('json', new Blob([JSON.stringify(json)], { type: 'application/json' }));
  return formData;
};

// 监听外部传入的videoUrls变化
watch(() => props.videoUrls, (newVal) => {
  localVideoUrls.value = [...(newVal || [])];
});

// 导出组件引用
defineExpose({
  handleDelete,
  clearAll: () => {
    localVideoUrls.value = [];
    emits('update:videoUrls', []);
  }
});
</script>

<style lang="less" scoped>
.video-uploader-container {
  .upload-box-list {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }

  .upload-box {
    position: relative;
    border: 2px dashed #d9d9d9;
    border-radius: 8px;
    overflow: hidden;
    transition: all 0.3s;

    &:hover {
      border-color: #1890ff;
    }

    .preview-video {
      width: 100%;
      height: 100%;
      object-fit: cover;
      cursor: pointer;
    }

    .play-icon {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 36px;
      color: rgba(255, 255, 255, 0.85);
      cursor: pointer;
      text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);

      &:hover {
        color: #ffffff;
      }
    }

    .delete-btn {
      position: absolute;
      top: 5px;
      right: 5px;
      width: 24px;
      height: 24px;
      background-color: rgba(0, 0, 0, 0.5);
      color: white;
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;

      &:hover {
        background-color: rgba(0, 0, 0, 0.7);
      }
    }
  }

  .upload-loading {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-style: solid;
    background: #fafafa;

    .loading-text {
      margin-top: 8px;
      color: #999;
      font-size: 13px;
    }
  }

  .upload-area {
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
  }

  .upload-hint {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 10px;

    .plus-icon {
      font-size: 32px;
      color: #1890ff;
      margin-bottom: 8px;
    }

    .hint-text {
      color: #999;
      font-size: 14px;
      text-align: center;
    }
  }
}

/* 预览弹窗样式 */
.preview-modal-content {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;

  .modal-video {
    max-width: 100%;
    max-height: 600px;
  }
}
</style>
